import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * Variant picker — multi-level option flow (Etnia PDP: LensName + Color + Size)
 *
 * ## Problem addressed
 * Rapid lens switching left color unselected and buy buttons stale because:
 * - Aborted fetches could still apply morph/update logic out of order
 * - `#radios` / `#checkedIndices` were only built in `connectedCallback` and went stale after morph
 * - `variant:update` was skipped while waiting for color auto-select, but auto-select sometimes never ran
 *
 * ## Lens change behaviour (fieldset 0, e.g. LensName)
 * 1. User selects a lens → fetch lens only → morph picker (all color swatches stay visible)
 * 2. Auto-click first color with `data-option-valid-for-lens` for that lens
 *
 * ## Color change behaviour (fieldset 1+)
 * User can click any color swatch. Cross-lens colors use `data-fallback-variant-id` so the
 * server morph updates lens + variant together (bidirectional lens ↔ color sync).
 *
 * ## Fast-click safety
 * - `#requestId` increments per fetch; stale responses are ignored after abort or superseded clicks
 * - `AbortError` on superseded requests does not reset `#shouldAutoSelectSecondLevel`
 *
 * ## Related files
 * - `snippets/variant-main-picker.liquid` — picker HTML + variant JSON script
 * - `assets/morph.js` — in-place DOM updates after section fetch
 * - `assets/product-form.js`, `assets/notify-me.js`, `assets/sticky-add-to-cart.js` — listen for `variant:update`
 *
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number} Monotonic id; only the latest `fetchUpdatedSection` response may apply updates. */
  #requestId = 0;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  /**
   * When true, a first-level (lens) change just completed a fetch and we may need to
   * auto-select a second-level (color) option before the variant is complete.
   */
  #shouldAutoSelectSecondLevel = false;

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      this.#checkedIndices.push(initialCheckedIndex === -1 ? [] : [initialCheckedIndex]);
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Rebuilds `#radios` and `#checkedIndices` from the live DOM.
   * Required after `morph()` replaces picker nodes; `connectedCallback` caches go stale.
   */
  #refreshRadioState() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    this.#radios = [];
    this.#checkedIndices = [];

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const checkedIndex = radios.findIndex(
        (radio) =>
          radio instanceof HTMLInputElement && (radio.checked || radio.dataset.currentChecked === 'true')
      );

      this.#checkedIndices.push(checkedIndex === -1 ? [] : [checkedIndex]);
    });
  }

  /**
   * After a lens change, auto-click the first color that exists for the new lens.
   * Does not use cross-lens fallbacks — those are only for manual color clicks.
   * @returns {HTMLInputElement | undefined}
   */
  #getFirstSecondLevelOption() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    if (fieldsets.length < 2) return undefined;

    const secondFieldset = fieldsets[1];
    if (!secondFieldset) return undefined;

    const secondLevelRadios = Array.from(secondFieldset.querySelectorAll('input[type="radio"]'));

    const validForLensRadio = secondLevelRadios.find(
      (radio) => radio instanceof HTMLInputElement && radio.dataset.optionValidForLens === 'true'
    );

    if (validForLensRadio instanceof HTMLInputElement) {
      return validForLensRadio.checked ? undefined : validForLensRadio;
    }

    return undefined;
  }

  /**
   * Updates `?variant=` on the PDP URL after the server resolves the variant.
   * @param {string | number | null | undefined} variantId
   * @param {{ pathname?: string }} [options]
   */
  #updateProductPageVariantUrl(variantId, { pathname } = {}) {
    if (this.dataset.templateProductMatch !== 'true') return;
    if (variantId == null || variantId === '') return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variantId));

    if (pathname) {
      url.pathname = pathname;
    }

    if (url.href === window.location.href) return;

    yieldToMainThread().then(() => {
      history.replaceState({}, '', url.toString());
    });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target instanceof HTMLInputElement && event.target.type !== 'radio') return;
    if (!(event.target instanceof HTMLInputElement) && !(event.target instanceof HTMLSelectElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;
    if (!selectedOption.dataset.optionValueId) return;

    this.updateSelectedOption(event.target);
    this.updateLensDescription(selectedOption);

    // True when the clicked value doesn't form a real variant with the other currently selected
    // options (e.g. this color isn't available for the currently selected lens). In that case the
    // other fieldsets are stale, so we need the server to resolve a valid combination instead of
    // requesting the (invalid) currently selected combination.
    let needsServerResolvedCombo = false;
    /** @type {string | undefined} */
    let fallbackVariantId;
    let deferVariantUrlUpdate = false;

    if (event.target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(event.target.dataset.fieldsetIndex || '');
      const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

      if (fieldsetIndex === 0 && fieldsets.length >= 2) {
        this.#shouldAutoSelectSecondLevel = true;
        needsServerResolvedCombo = true;
        deferVariantUrlUpdate = true;
      } else if (fieldsetIndex > 0 && !event.target.dataset.variantId) {
        needsServerResolvedCombo = true;
        // Server-rendered hint pointing at a real variant carrying this value, used to request the
        // exact variant directly instead of letting Shopify's option_values matching guess at one —
        // that fuzzy matching was landing on the wrong variant (e.g. the first one sharing this value).
        fallbackVariantId = event.target.dataset.fallbackVariantId || undefined;
      }
    }

    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const requestSource = fallbackVariantId ? 'variant-id' : needsServerResolvedCombo ? 'option-value-only' : null;

    this.fetchUpdatedSection(
      this.buildRequestUrl(selectedOption, requestSource, [], fallbackVariantId),
      morphElementSelector,
      { loadsNewProduct, newUrl: loadsNewProduct ? newUrl : undefined }
    );

    if (isOnProductPage && !deferVariantUrlUpdate) {
      const urlVariantId = selectedOption.dataset.variantId || fallbackVariantId || null;

      if (urlVariantId) {
        this.#updateProductPageVariantUrl(urlVariantId, {
          pathname: loadsNewProduct ? newUrl : undefined,
        });
      } else {
        const url = new URL(window.location.href);
        url.searchParams.delete('variant');
        if (loadsNewProduct && newUrl) {
          url.pathname = newUrl;
        }
        if (url.href !== window.location.href) {
          yieldToMainThread().then(() => {
            history.replaceState({}, '', url.toString());
          });
        }
      }
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Replaces the external lens description mount with a cloned pre-rendered template.
   * The normal variant fetch still runs and remains the source of truth for the picker itself.
   * @param {HTMLOptionElement | HTMLInputElement} selectedOption
   */
  updateLensDescription(selectedOption) {
    const templateId = selectedOption.dataset.lensTemplateId;
    if (!templateId) return;

    const wrapper = this.closest('.variant-picker__wrapper');
    if (!(wrapper instanceof HTMLElement)) return;

    const host = wrapper.querySelector('[data-product-lens-description-host]');
    if (!(host instanceof HTMLElement)) return;

    const template = wrapper.querySelector(`template[data-lens-description-template="${CSS.escape(templateId)}"]`);
    if (!(template instanceof HTMLTemplateElement)) return;

    host.replaceChildren(template.content.cloneNode(true));
  }

  /**
   * Gets the section rendering id used for product-card variant updates.
   * Uses the current card gallery mode so the server only renders the active gallery implementation.
   * @returns {string}
   */
  getSectionRenderingId() {
    const productCard = this.closest('product-card');
    const hasQuickAdd = productCard?.querySelector('[ref="cardGallery"] quick-add-component') != null;
    const cardGalleryMode = productCard?.querySelector('[ref="cardGallery"]')?.getAttribute('data-card-gallery-mode');

    if (cardGalleryMode === 'preview') {
      return hasQuickAdd
        ? 'section-rendering-product-card-preview-quick-add'
        : 'section-rendering-product-card-preview';
    }

    if (hasQuickAdd) {
      return 'section-rendering-product-card-quick-add';
    }

    return 'section-rendering-product-card';
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source. `'variant-id'` requests a specific known-good
   *   variant directly (most reliable). `'option-value-only'` requests just the clicked option value
   *   (ignoring the other currently selected options), letting the server guess the rest — used as a
   *   fallback when no specific variant id is known. Both are used when the clicked value has no real
   *   variant with the current selection.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @param {string} [fallbackVariantId] - Variant id to request directly when `source` is `'variant-id'`.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], fallbackVariantId) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (source === 'variant-id' && fallbackVariantId) {
      params.push(`variant=${fallbackVariantId}`);
    } else if (source === 'option-value-only') {
      params.push(`option_values=${selectedOption.dataset.optionValueId}`);
    } else if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const sectionRenderingId = this.getSectionRenderingId();
    const SECTION_ID_MAP = {
      'quick-add-component': sectionRenderingId,
      'swatches-variant-picker-component': sectionRenderingId,
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {{ loadsNewProduct?: boolean, newUrl?: string }} [urlContext]
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, urlContext = {}) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const requestId = ++this.#requestId;

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        if (requestId !== this.#requestId) return;

        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        /** @type {{ id?: number | string } | null} */
        let resolvedVariant = null;
        try {
          resolvedVariant = JSON.parse(textContent);
        } catch {
          resolvedVariant = null;
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        if (this.#shouldAutoSelectSecondLevel) {
          this.#refreshRadioState();
        }

        const secondLevelTarget = this.#shouldAutoSelectSecondLevel
          ? this.#getFirstSecondLevelOption()
          : undefined;
        const isAutoSelectingSecondLevel = Boolean(secondLevelTarget);

        // Skip the temporary incomplete state; the auto-clicked second-level option will dispatch the real variant update.
        if (this.selectedOptionId && !isAutoSelectingSecondLevel) {
          this.dispatchEvent(
            new VariantUpdateEvent(resolvedVariant, this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );

          // Lens / cross-lens flows often lack data-variant-id on the clicked input — sync from server response.
          this.#updateProductPageVariantUrl(resolvedVariant?.id, {
            pathname: urlContext.loadsNewProduct ? urlContext.newUrl : undefined,
          });
        }

        if (isAutoSelectingSecondLevel) {
          this.#shouldAutoSelectSecondLevel = false;
          requestAnimationFrame(() => {
            if (requestId !== this.#requestId) return;
            this.#refreshRadioState();
            const target = this.#getFirstSecondLevelOption();
            if (target instanceof HTMLInputElement) {
              target.click();
              return;
            }

            if (this.selectedOptionId) {
              this.dispatchEvent(
                new VariantUpdateEvent(resolvedVariant, this.selectedOptionId, {
                  html,
                  productId: this.dataset.productId ?? '',
                  newProduct,
                })
              );
            }

            this.#updateProductPageVariantUrl(resolvedVariant?.id, {
              pathname: urlContext.loadsNewProduct ? urlContext.newUrl : undefined,
            });
          });
        } else {
          this.#shouldAutoSelectSecondLevel = false;
        }
      })
      .catch((error) => {
        if (requestId !== this.#requestId) return;

        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
          return;
        }

        console.error(error);
        this.#shouldAutoSelectSecondLevel = false;
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });

    const wrapper = this.closest('.variant-picker__wrapper');
    if (wrapper) {
      const lensDescriptionHost = wrapper.querySelector('[data-product-lens-description-host]');
      const newLensDescriptionHost = newHtml.querySelector('[data-product-lens-description-host]');
      if (lensDescriptionHost && newLensDescriptionHost) {
        morph(lensDescriptionHost, newLensDescriptionHost);
      }
    }

    this.#refreshRadioState();
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
